import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  EyeIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowPathIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import api from '../utils/api';
import { formatDate, formatDateFull } from '../utils/dateUtils';
import toast from 'react-hot-toast';

const NOCDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('waiting');
  const [waitingRequests, setWaitingRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [nocComments, setNocComments] = useState('');
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'

  // Fetch NOC dashboard stats
  const fetchStats = async () => {
    try {
      const response = await api.get('/noc/dashboard/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching NOC stats:', error);
      toast.error('Failed to load dashboard stats');
    }
  };

  // Fetch waiting requests
  const fetchWaitingRequests = async () => {
    try {
      const response = await api.get('/noc/requests/waiting');
      if (response.data.success) {
        setWaitingRequests(response.data.data.requests);
      }
    } catch (error) {
      console.error('Error fetching waiting requests:', error);
      toast.error('Failed to load waiting requests');
    }
  };

  // Fetch all NOC requests
  const fetchAllRequests = async () => {
    try {
      const response = await api.get('/noc/requests');
      if (response.data.success) {
        setAllRequests(response.data.data.requests);
      }
    } catch (error) {
      console.error('Error fetching all requests:', error);
      toast.error('Failed to load requests');
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchWaitingRequests(),
        fetchAllRequests()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Handle approve/reject actions
  const handleAction = async (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setNocComments('');
    setShowModal(true);
  };

  const confirmAction = async () => {
    if (!selectedRequest || !actionType) return;
    
    if (actionType === 'reject' && nocComments.trim().length < 10) {
      toast.error('Please provide detailed comments for rejection (min 10 characters)');
      return;
    }

    setActionLoading(selectedRequest.id);
    
    try {
      const endpoint = `/noc/requests/${selectedRequest.id}/${actionType}`;
      const payload = { noc_comments: nocComments.trim() || `${actionType === 'approve' ? 'Approved' : 'Rejected'} by NOC` };
      
      const response = await api.put(endpoint, payload);
      
      if (response.data.success) {
        toast.success(`Request ${actionType === 'approve' ? 'approved' : 'rejected'} successfully`);
        
        // Refresh data
        await Promise.all([
          fetchStats(),
          fetchWaitingRequests(),
          fetchAllRequests()
        ]);
        
        setShowModal(false);
        setSelectedRequest(null);
        setActionType('');
        setNocComments('');
      }
    } catch (error) {
      console.error(`Error ${actionType}ing request:`, error);
      toast.error(`Failed to ${actionType} request`);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'admin_approved': 'bg-blue-100 text-blue-800 border-blue-200',
      'waiting_noc': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'noc_approved': 'bg-purple-100 text-purple-800 border-purple-200',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'rejected': 'bg-red-100 text-red-800 border-red-200'
    };
    
    const labels = {
      'pending': 'Pending Review',
      'admin_approved': 'Admin Approved',
      'waiting_noc': 'Awaiting NOC Review',
      'noc_approved': 'NOC Approved',
      'completed': 'Completed',
      'rejected': 'Rejected'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${badges[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'waiting', name: 'Waiting for NOC', count: waitingRequests.length, icon: ClockIcon },
    { id: 'all', name: 'All Requests', count: allRequests.length, icon: DocumentTextIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Made consistent with Admin Dashboard */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="h-8 w-8 mr-3">
                <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Garuda Media" className="w-full h-full object-contain" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">
                NOC Panel - Garuda Media
              </h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Welcome, <span className="font-medium">{user?.username}</span>
              </div>
              <div className="flex items-center space-x-2">
                {user?.role === 'superadmin' && (
                  <a
                    href="/admin"
                    className="btn-secondary text-sm flex items-center"
                    title="Switch to Admin Dashboard"
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    Admin Panel
                  </a>
                )}
                <button
                  onClick={() => window.location.reload()}
                  className="btn-secondary text-sm flex items-center"
                  title="Refresh Data"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-1" />
                  Refresh
                </button>
                <button
                  onClick={logout}
                  className="btn-secondary text-sm"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Waiting for NOC</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.waiting_noc || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIconSolid className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">NOC Approved</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.noc_approved || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.completed || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">My Approvals</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.my_approvals || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white shadow rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.name}
                    {tab.count > 0 && (
                      <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                        activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-900'
                      }`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {activeTab === 'waiting' && (
              <div className="space-y-4">
                {waitingRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No requests waiting</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      All requests have been processed or there are no new requests.
                    </p>
                  </div>
                ) : (
                  waitingRequests.map((request) => (
                    <div key={request.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-medium text-gray-900">
                              {request.user?.company_name || request.user?.username}
                            </h3>
                            {getStatusBadge(request.status)}
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                            <div>
                              <strong>Account ID:</strong> {request.account_id}
                            </div>
                            <div>
                              <strong>Order ID:</strong> {request.order_id || 'N/A'}
                            </div>
                            <div>
                              <strong>Current:</strong> {request.current_bandwidth}
                            </div>
                            <div>
                              <strong>Requested:</strong> {request.requested_bandwidth}
                            </div>
                          </div>

                          <div className="text-sm text-gray-600 mb-3">
                            <div><strong>Contact:</strong> {request.user?.contact_person || 'N/A'}</div>
                            <div><strong>Admin Approved:</strong> {formatDate(request.updated_at)}</div>
                            {request.admin_comments && (
                              <div><strong>Admin Comments:</strong> {request.admin_comments}</div>
                            )}
                          </div>
                        </div>

                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => handleAction(request, 'approve')}
                            disabled={actionLoading === request.id}
                            className="btn-primary text-sm flex items-center"
                          >
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleAction(request, 'reject')}
                            disabled={actionLoading === request.id}
                            className="btn-danger text-sm flex items-center"
                          >
                            <XCircleIcon className="h-4 w-4 mr-1" />
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'all' && (
              <div className="space-y-4">
                {allRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No requests found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      No requests have been assigned to NOC yet.
                    </p>
                  </div>
                ) : (
                  allRequests.map((request) => (
                    <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-medium text-gray-900">
                              {request.user?.company_name || request.user?.username}
                            </h3>
                            {getStatusBadge(request.status)}
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                            <div>
                              <strong>Account ID:</strong> {request.account_id}
                            </div>
                            <div>
                              <strong>Order ID:</strong> {request.order_id || 'N/A'}
                            </div>
                            <div>
                              <strong>Current:</strong> {request.current_bandwidth}
                            </div>
                            <div>
                              <strong>Requested:</strong> {request.requested_bandwidth}
                            </div>
                          </div>

                          <div className="text-sm text-gray-600">
                            <div><strong>Created:</strong> {formatDate(request.created_at)}</div>
                            {request.nocApprover && (
                              <div><strong>NOC Approved by:</strong> {request.nocApprover.username}</div>
                            )}
                            {request.noc_comments && (
                              <div><strong>NOC Comments:</strong> {request.noc_comments}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {actionType === 'approve' ? 'Approve' : 'Reject'} Request
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Company: <strong>{selectedRequest?.user?.company_name}</strong>
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Bandwidth: {selectedRequest?.current_bandwidth} → {selectedRequest?.requested_bandwidth}
              </p>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                NOC Comments {actionType === 'reject' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                value={nocComments}
                onChange={(e) => setNocComments(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={actionType === 'approve' ? 'Optional comments...' : 'Required: Please provide reason for rejection...'}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="btn-secondary text-sm"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={confirmAction}
                disabled={actionLoading}
                className={`text-sm ${
                  actionType === 'approve' ? 'btn-primary' : 'btn-danger'
                } ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {actionLoading ? 'Processing...' : (actionType === 'approve' ? 'Approve' : 'Reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NOCDashboard;